import React from "react";

/**
 * RetiredStrategyCard — 폐기된 전략 1건을 표시하는 카드.
 *
 * Overview 페이지의 "폐기됨" 탭과 /research/retired 카탈로그에서 공유.
 *
 * 시각 분류 (트랙 E):
 *   - 실손실형 (finalStats.pnl_krw < 0): border-l-2 border-l-loss 강조,
 *     손실액을 text-base font-bold 로 카드 헤더 우측에 크게 표시.
 *   - 통합형 (pnl_krw 미정의): loss 색상 제거, "[통합]" 배지 표시.
 *
 * finalStats 는 key/value 직렬화 문자열 대신 `dl grid grid-cols-2`
 * 정렬로 렌더링해 스캔 가독성을 높인다 (pnl_krw 는 헤더에서 강조 표시되므로
 * 본문 dl 에서 제외).
 *
 * props:
 *   - strategy: 폐기 항목 ({id, name, paradigm, summary, retiredAt,
 *     retiredReason, finalStats}) — 백엔드 레코드 + strategyMeta 머지 결과
 *   - onClick(id): 카드 클릭 시 호출 (디테일 이동)
 *   - onRestore(id): 있으면 "복원" 버튼 노출 (운영 Overview 컨텍스트). 정적
 *     카탈로그(/research/retired)에서는 전달하지 않아 버튼이 숨겨진다.
 *   - restoring: 이 카드의 복원 API 진행 중 여부
 */
export default function RetiredStrategyCard({ strategy, onClick, onRestore, restoring }) {
    const handle = () => onClick && onClick(strategy.id);
    const stats = strategy.finalStats ?? null;
    const pnl = stats?.pnl_krw;
    const hasPnl = typeof pnl === "number";
    const isLoss = hasPnl && pnl < 0;
    const isGain = hasPnl && pnl > 0;

    // 실손실형은 좌측 loss accent + 경계 강조. 통합형은 중립 surface border.
    const borderClass = isLoss
        ? "border border-loss/40 border-l-2 border-l-loss"
        : "border border-surface-border";

    // finalStats 의 보조 필드 (pnl_krw 제외) — 2열 dl 그리드용.
    const extraStats = stats
        ? Object.entries(stats).filter(([k]) => k !== "pnl_krw")
        : [];

    return (
        <div
            className={`relative bg-surface-card rounded-lg transition-colors ${borderClass}`}
        >
            <button
                type="button"
                onClick={handle}
                className="text-left w-full block p-3 rounded-lg hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-brand"
                aria-label={`${strategy.id} 상세 보기${
                    isLoss ? ` (확정 손실 ${pnl.toLocaleString()} KRW)` : " (통합 정리)"
                }`}
            >
            <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-content">{strategy.id}</span>
                    {strategy.paradigm && (
                        <span className="text-xs text-content-secondary bg-surface-elevated px-1.5 py-0.5 rounded shrink-0">
                            {strategy.paradigm}
                        </span>
                    )}
                    {!hasPnl && (
                        <span
                            className="text-xs bg-surface-elevated text-content-muted px-2 py-0.5 rounded shrink-0"
                            title="trigger 중복 또는 변형 통합으로 별도 손실 없이 폐기"
                        >
                            통합
                        </span>
                    )}
                </div>
                <div className="text-right shrink-0">
                    {hasPnl ? (
                        <div
                            className={`text-base font-bold tabular-nums leading-tight ${
                                isLoss ? "text-loss" : isGain ? "text-gain" : "text-content-muted"
                            }`}
                            aria-label="확정 손익 (KRW)"
                        >
                            {pnl.toLocaleString()} KRW
                        </div>
                    ) : (
                        <div className="text-xs text-content-muted">손익 없음</div>
                    )}
                    {strategy.retiredAt && (
                        <div className="text-xs text-content-muted leading-tight">
                            {strategy.retiredAt}
                        </div>
                    )}
                </div>
            </div>
            {strategy.name && (
                <div className="text-sm text-content-secondary mb-1.5">{strategy.name}</div>
            )}
            {strategy.summary && (
                <p className="text-xs text-content-secondary leading-relaxed mb-2 line-clamp-3">
                    {strategy.summary}
                </p>
            )}
            {strategy.retiredReason && (
                <div className="text-xs text-loss mt-2">
                    <span className="text-content-muted">사유: </span>
                    {strategy.retiredReason}
                </div>
            )}
            {extraStats.length > 0 && (
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 pt-2 border-t border-surface-border text-xs">
                    {extraStats.map(([k, v]) => (
                        <div key={k} className="flex items-baseline justify-between gap-2 min-w-0">
                            <dt className="text-content-muted shrink-0">{k}</dt>
                            <dd className="text-content-secondary tabular-nums truncate">
                                {typeof v === "number" ? v.toLocaleString() : v}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
            </button>
            {onRestore && (
                <div className="flex justify-end px-3 pb-2 -mt-1">
                    <button
                        type="button"
                        onClick={() => !restoring && onRestore(strategy.id)}
                        disabled={restoring}
                        aria-label={`${strategy.id} 운영으로 복원`}
                        className="text-xs text-content-secondary hover:text-content border border-surface-border rounded px-2 py-1 min-h-11 hover:bg-surface-hover disabled:opacity-50 transition-colors"
                    >
                        {restoring ? "복원 중…" : "↩ 복원"}
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * 폐기 전략 리스트의 확정 손실 합계(KRW). 통합형(pnl_krw 미정의)은 0 으로 취급.
 * 단일 합계가 필요한 화면용. 분리 표시가 필요하면 splitRetiredPnL 사용.
 */
export function totalRetiredPnL(retired) {
    return retired.reduce((acc, s) => acc + (Number(s?.finalStats?.pnl_krw) || 0), 0);
}

/**
 * 단독 paper 손실(실손실형 합계) 과 통합 정리 건수를 분리해 반환.
 * RetiredSection / PaperRetiredPage 헤더 KPI 에서 사용.
 */
export function splitRetiredPnL(retired) {
    let lossSum = 0;
    let lossCount = 0;
    let integrationCount = 0;
    for (const s of retired) {
        const v = s?.finalStats?.pnl_krw;
        if (typeof v === "number") {
            lossSum += v;
            lossCount += 1;
        } else {
            integrationCount += 1;
        }
    }
    return { lossSum, lossCount, integrationCount };
}

/**
 * groupRetiredByParadigm — RetiredStrategyCard 리스트를 paradigm 별로 그룹화.
 * Overview 탭 / Retired 페이지 양쪽에서 동일 정렬 보장.
 */
export const PARADIGM_ORDER = [
    "Macro",
    "Vol5m",
    "Liq",
    "OI",
    "Basis",
    "Cross-coin",
    "Variant",
];

export function groupRetiredByParadigm(retired) {
    const out = {};
    for (const s of retired) {
        const key = s.paradigm ?? "기타";
        if (!out[key]) out[key] = [];
        out[key].push(s);
    }
    return out;
}
